export interface MaterialTextureOrderSource {
  order?: number
  slot_name?: string | null
}

/** Parse `BedMatSlot_N` (case-insensitive) from a material or texture slot name. */
export function parseBedMatSlotNumber(name: string | null | undefined): number | null {
  if (!name) return null
  const match = name.match(/bedmatslot_(\d+)/i)
  if (!match) return null
  const parsed = parseInt(match[1] || '', 10)
  return Number.isFinite(parsed) ? parsed : null
}

/** Stable sort key: admin `order` first, then BedMatSlot number, then slot_name. */
export function materialTextureSortKey(entry: MaterialTextureOrderSource): [number, number, string] {
  const order = entry.order ?? 0
  const slotNum = parseBedMatSlotNumber(entry.slot_name) ?? Number.MAX_SAFE_INTEGER
  return [order, slotNum, (entry.slot_name || '').toLowerCase()]
}

export function sortMaterialTextures<T extends MaterialTextureOrderSource>(textures: T[]): T[] {
  return [...textures].sort((a, b) => {
    const ka = materialTextureSortKey(a)
    const kb = materialTextureSortKey(b)
    for (let i = 0; i < ka.length; i += 1) {
      if (ka[i] < kb[i]) return -1
      if (ka[i] > kb[i]) return 1
    }
    return 0
  })
}

/**
 * Find the texture map index for a bed material slot number (N in BedMatSlot_N).
 * Prefers explicit slot_name match, then N-1 in the sorted texture list.
 */
export function findTextureIndexForBedMatSlot(
  bedMatSlotNumber: number,
  textures: Array<{ slot_name?: string | null }>,
): number {
  if (!textures.length || bedMatSlotNumber < 1) return -1

  const byName = textures.findIndex(
    (entry) => parseBedMatSlotNumber(entry.slot_name) === bedMatSlotNumber,
  )
  if (byName >= 0) return byName

  const sortedIndex = bedMatSlotNumber - 1
  if (sortedIndex < textures.length) return sortedIndex

  return sortedIndex % textures.length
}
